/**
 * Routes. `/` chooses or creates a profile; `/p/:id/*` is one profile seen
 * through whatever relation the signed-in person has to it (the API decides).
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { hasSession, onSessionChange } from '@/core/auth';
import { SignInPage } from '@/features/access/SignInPage';
import { ProfilesPage } from '@/features/profiles/ProfilesPage';
import { NewProfilePage } from '@/features/profiles/NewProfilePage';
import { TodayPage } from '@/features/today/TodayPage';
import { MapPage } from '@/features/map/MapPage';
import { PathPage } from '@/features/path/PathPage';
import { TimelinePage } from '@/features/timeline/TimelinePage';
import { SharePage } from '@/features/share/SharePage';
import { RedeemPage } from '@/features/share/RedeemPage';
import { BaselinePage } from '@/features/baseline/BaselinePage';
import { ProfileShell } from './ProfileShell';

function useSignedIn(): boolean {
  const [v, setV] = useState(hasSession());
  useEffect(() => onSessionChange(() => setV(hasSession())), []);
  return v;
}

function Root() {
  return <Outlet />;
}

function Authed() {
  const signedIn = useSignedIn();
  if (!signedIn) return <Navigate to="/signin" />;
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: Root });
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  component: SignInPage,
});
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: Authed,
});
const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: ProfilesPage,
});
const newRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/new',
  component: NewProfilePage,
});
const redeemRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/redeem',
  component: RedeemPage,
});
const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/p/$id',
  component: ProfileShell,
});
const todayRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/',
  component: TodayPage,
});
const mapRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/map',
  component: MapPage,
});
const pathRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/path/$branch',
  component: PathPage,
});
const timelineRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/timeline',
  component: TimelinePage,
});
const shareRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/share',
  component: SharePage,
});
const baselineRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: '/baseline',
  component: BaselinePage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  authedRoute.addChildren([
    homeRoute,
    newRoute,
    redeemRoute,
    profileRoute.addChildren([
      todayRoute,
      mapRoute,
      pathRoute,
      timelineRoute,
      shareRoute,
      baselineRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
